import type { ResponsivePerPage } from "../layout/Carousel";
import type { GridProps } from "../layout/Grid";
import type { ColumnType } from "./mapColumns";

const BREAKPOINTS = {
  mobile: 0,
  largeMobile: 576,
  tablet: 768,
  desktop: 992,
  wide: 1200,
} as const;

type Breakpoint = keyof typeof BREAKPOINTS;

type GetImageSizesProps = {
  carouselCardSize?: GridProps['carouselCardSize'];
  columns?: Record<string, ColumnType | null>;
  perPage?: ResponsivePerPage;
}

/**
 * Retrieves the appropriate responsive image sizes string based on the provided properties.
 *
 * - If `carouselCardSize` is specified, returns sizes suitable for a carousel card.
 * - If `columns` is specified, returns sizes suitable for displaying items in columns.
 * - If `perPage` is specified, returns sizes suitable for a paginated carousel view.
 * - Returns `undefined` if none of the relevant properties are provided.
 *
 * @param props - Object containing either `carouselCardSize`, `columns`, or `perPage`.
 * @returns {string | undefined} A string representing image sizes or undefined if no valid parameters provided
 */
export const getImageSizes = ({ carouselCardSize, columns, perPage }: GetImageSizesProps): string | undefined => {
  if (carouselCardSize) {
    return getImageSizesBasedOnCarouselCardSize(carouselCardSize);
  }

  if (columns) {
    return getImageSizesBasedOnColumns(columns);
  }

  if (perPage) {
    return getImageSizesForCarousel(perPage);
  }

  return undefined;
}

/**
 * Computes a string representing responsive image sizes based on carousel card size
 *
 * @param {string} carouselCardSize - The size of the carousel card ('small' or 'large')
 * @returns {string | undefined} A string representing image sizes or undefined if no valid parameters provided
 *
 * @remarks
 * The width values need to be kept in sync with Grid styles.
 */
export const getImageSizesBasedOnCarouselCardSize = (carouselCardSize?: GetImageSizesProps['carouselCardSize']): string | undefined => {
  if (!carouselCardSize) return undefined;

  // needs to be in sync with Grid styles for columns width
  return carouselCardSize === 'small'
    ? '(min-width: 768px) calc(100vw / 3), 80vw'
    : '(min-width: 768px) 50vw, 100vw';
}

/**
 * Computes a string representing responsive image sizes based on the provided column configuration.
 *
 * @param columns - An object mapping breakpoint names to the number of columns used at each breakpoint
 * @returns {string | undefined} A string representing image sizes or undefined if no valid parameters provided
 */
export const getImageSizesBasedOnColumns = (columns: GetImageSizesProps['columns']): string | undefined => {
  if (!columns) return undefined;

  const sizes = prepareBreakpointBasedConfig(columns)
    .map(([key, value]) => {
      if (key === 'mobile') {
        return value ? `calc(100vw / ${value})` : '100vw';
      }

      return value ? `(min-width: ${BREAKPOINTS[key]}px) calc(100vw / ${value})` : undefined;
    })
    .filter(Boolean);

  return sizes.length ? sizes.join(', ') : undefined;
}

/**
 * Computes a string representing responsive image sizes based on the provided perPage configuration.
 *
 * @param perPageArg - A mapping of breakpoints to the number of items that should appear in the carousel.
 * @returns {string | undefined} A string representing image sizes or undefined if no valid parameters provided
 */
export const getImageSizesForCarousel = (perPageArg?: ResponsivePerPage): string | undefined => {
  if (!perPageArg) return undefined;

  // create a mutable copy to avoid modifying the perPageArg object directly.
  // use a more general type for the copy to allow adding 'default' to mobile.
  const perPage: Partial<Record<Breakpoint, number | string | undefined>> = { ...perPageArg };

  if (!perPage.mobile) {
    perPage.mobile = 'default';
  }

  const sizes = prepareBreakpointBasedConfig(perPage)
    .map(([key, value]) => {
      if (key === 'mobile') {
        return getImageSizesForCarouselMobile(value as ResponsivePerPage['mobile']);
      }

      return value ? `(min-width: ${BREAKPOINTS[key]}px) calc(100vw / ${value})` : undefined;
    })
    .filter(Boolean);

  return sizes.length ? sizes.join(', ') : undefined;
};

/**
 * Creates an array of breakpoint-value pairs from the given record, filtering
 * out invalid breakpoint keys and returning the result in descending breakpoint order.
 *
 * @template TValue - Type of the record's values.
 * @param v - A record of breakpoint strings to their associated values.
 * @returns A sorted array of breakpoint-value tuples, where the breakpoints are valid
 *          and sorted from largest to smallest.
 */
const prepareBreakpointBasedConfig = <TValue>(v: Record<string, TValue>): [Breakpoint, TValue][] => {
  return Object.entries(v)
    // ensure keys are Breakpoints
    .filter((entry): entry is [Breakpoint, typeof entry[1]] => {
      const key = entry[0];
      return key in BREAKPOINTS;
    })
    // sort from the largest to smallest breakpoint
    .sort(([a], [b]) => BREAKPOINTS[b] - BREAKPOINTS[a]);
};

/**
 * Returns the appropriate carousel image width for mobile devices, based on
 * the specified per-page responsive setting. The value determines the target
 * width in either viewport-wide units or fixed pixel size.
 *
 * @param v - The mobile setting used to determine the image width
 * @returns The image size as a string (e.g., "100vw", "256px", etc.)
 *
 * @remarks
 * The width values need to be kept in sync with Carousel styles.
 */
const getImageSizesForCarouselMobile = (v: ResponsivePerPage['mobile']): string => {
  // needs to be in sync with Carousel styles
  switch (v) {
    case 'full':
      return '100vw';
    case 'small':
      return '256px';
    case 'large':
      return '320px';
    default:
      return '278px';
  }
}
